import React from 'react';

import { CheckCircle, HourglassEmpty } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Clear';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import { MakeMeAnOfferStepStatus } from '~/services/AccommodationService';

export interface MakeMeAnOfferCreateStatusProps {
  name?: string;
  offerUrl?: string;
  errorMessage?: string;
  flowType?: 'copy' | 'new' | 'import' | null;
  status: {
    // Regular flow fields
    createOffer?: MakeMeAnOfferStepStatus;
    createProperty?: MakeMeAnOfferStepStatus;
    createPackages?: MakeMeAnOfferStepStatus;
    createSchedules?: MakeMeAnOfferStepStatus;
    createAvailability?: MakeMeAnOfferStepStatus;
    // Prod upsert flow fields
    validateOffer?: MakeMeAnOfferStepStatus;
    validateProperty?: MakeMeAnOfferStepStatus;
    validateVendor?: MakeMeAnOfferStepStatus;
    upsertVendor?: MakeMeAnOfferStepStatus;
    upsertProperty?: MakeMeAnOfferStepStatus;
    upsertOffer?: MakeMeAnOfferStepStatus;
    resyncOffer?: MakeMeAnOfferStepStatus;
  };
}

export default function MakeMeAnOfferCreateStatus({
  name,
  offerUrl,
  errorMessage,
  flowType,
  status,
}: MakeMeAnOfferCreateStatusProps) {
  // Use explicit flowType instead of detecting from status
  const isProdUpsertFlow = flowType === 'import';

  const steps = isProdUpsertFlow
    ? [
        { label: 'Validate Offer', status: status.validateOffer },
        { label: 'Validate Property', status: status.validateProperty },
        { label: 'Validate Vendor', status: status.validateVendor },
        { label: 'Upsert Vendor', status: status.upsertVendor },
        { label: 'Upsert Property', status: status.upsertProperty },
        { label: 'Upsert Offer', status: status.upsertOffer },
        { label: 'Resync Offer', status: status.resyncOffer },
      ]
    : [
        { label: 'Offer Created', status: status.createOffer },
        { label: 'Property Created', status: status.createProperty },
        { label: 'Packages Created', status: status.createPackages },
        { label: 'Schedules Created', status: status.createSchedules },
        { label: 'Availability Set', status: status.createAvailability },
      ];

  const allCompleted = steps.every((step) => step.status === 'completed');

  return (
    <Box>
      <Typography variant="h6">Offer Creation Status</Typography>

      <List>
        {steps.map((step, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {step.status === 'completed' && <CheckCircle color="success" />}
              {step.status === 'awaiting' && <HourglassEmpty color="disabled" />}
              {step.status === 'processing' && <CircularProgress size={20} />}
              {step.status === 'failed' && <DeleteIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary={step.label} />
          </ListItem>
        ))}
      </List>

      {allCompleted && (
        <Alert severity="success">
          <AlertTitle>Offer Successfully Created!</AlertTitle>
          <Link href={offerUrl} target="_blank">
            {name}
          </Link>
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
}
